import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useSnackbar } from "notistack";
import useAxiosPrivate from "../../Hooks/useAxiosPrivate";
import { formatMoney } from "../../utils/helpers";

const moneyFormat = (value) => formatMoney(value);

const dateFormat = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
};

const EventInvoices = () => {
  const { attendeeId } = useParams();
  const axiosPrivate = useAxiosPrivate();
  const { enqueueSnackbar } = useSnackbar();

  const [invoices, setInvoices] = useState([]);
  const [eventTitle, setEventTitle] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      setIsLoading(true);
      setError(false);
      try {
        const resp = await axiosPrivate.get(`/api/attendees/${attendeeId}/invoices`);
        const data = resp.data;
        
        if (!cancelled) {
          // Handle both old array format and new object format
          if (Array.isArray(data)) {
            // Old format: array of invoices
            setInvoices(data);
            if (data.length > 0 && data[0].eventTitle) {
              setEventTitle(data[0].eventTitle);
            }
          } else if (data && typeof data === 'object') {
            // New format: { eventTitle, invoices }
            setEventTitle(data.eventTitle || 'Event');
            setInvoices(Array.isArray(data.invoices) ? data.invoices : []);
          } else {
            setInvoices([]);
          }
        }
      } catch (e) {
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [axiosPrivate, attendeeId]);

  const handleDownload = async (invoice) => {
    if (!invoice?.pdfUrl) {
      enqueueSnackbar("Invoice PDF not available", { variant: "info" });
      return;
    }

    try {
      
      // Use axiosPrivate to include auth token
      const response = await axiosPrivate.get(invoice.pdfUrl, {
        responseType: 'blob'
      });


      // Create blob link to download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice-${invoice.invoiceNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);

      enqueueSnackbar("Invoice downloaded successfully", { variant: "success" });
    } catch (error) {
      console.error("Error downloading invoice:", error);
      console.error("Error details:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      enqueueSnackbar(`Failed to download invoice: ${error.response?.status || error.message}`, { variant: "error" });
    }
  };

  if (isLoading) {
    return (
      <main>
        <div className="full-row light-blue">
          <div className="container-fluid">
            <div className="row text-white pt-3 pb-3">
              <div className="col-md-12 text-white">
                <h1 className="text-uppercase">Invoices</h1>
                <p>View and download past payments</p>
              </div>
            </div>
          </div>
        </div>
        <div className="full-row white">
          <div className="container-fluid">
            <div className="row mt-5">
              <div className="col-md-12 text-center">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-3">Loading invoices...</p>
              </div>
            </div>
          </div>
        </div>
      </main>
    );
  }

  if (error) {
    return (
      <main>
        <div className="full-row light-blue">
          <div className="container-fluid">
            <div className="row text-white pt-3 pb-3">
              <div className="col-md-12 text-white">
                <h1 className="text-uppercase">Invoices</h1>
                <p>View and download past payments</p>
              </div>
            </div>
          </div>
        </div>
        <div className="full-row white">
          <div className="container-fluid">
            <div className="row mt-5">
              <div className="col-md-12">
                <div className="alert alert-danger" role="alert">
                  Error occurred while fetching invoices.
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main>
      <div className="full-row light-blue">
        <div className="container-fluid">
          <div className="row text-white pt-3 pb-3">
            <div className="col-md-12 text-white">
              <h1 className="text-uppercase">Invoices</h1>
              <p>View and download past payments</p>
            </div>
          </div>
        </div>
      </div>
      <div className="full-row white">
        <div className="container-fluid">
          <div className="row mt-5">
            <div className="col-md-12 mb-4">
              <h3>{eventTitle || "Event"} Invoices</h3>
            </div>
            <div className="col-md-12">
              {invoices.length === 0 ? (
                <div className="alert alert-info" role="alert">
                  No payment transactions found for this registration.
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover">
                    <thead>
                      <tr>
                        <th scope="col">Date</th>
                        <th scope="col">Transaction ID</th>
                        <th scope="col">Amount</th>
                        <th scope="col">Status</th>
                        <th scope="col">Card</th>
                        <th scope="col">Name</th>
                        <th scope="col">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invoices.map((invoice, index) => (
                        <tr key={invoice.invoiceNumber || invoice.transactionId || index}>
                          <td>{dateFormat(invoice.invoiceDate)}</td>
                          <td>{invoice.invoiceNumber || "—"}</td>
                          <td>{moneyFormat(invoice.amount)}</td>
                          <td>
                            <span className={`badge ${invoice.status === 'Approved' ? 'bg-success' : invoice.status === 'Declined' ? 'bg-danger' : 'bg-secondary'}`}>
                              {invoice.status || 'Unknown'}
                            </span>
                          </td>
                          <td>{invoice.cardLastFour ? `****${invoice.cardLastFour}` : '—'}</td>
                          <td>{invoice.billingName || '—'}</td>
                          <td>
                            {invoice.pdfUrl ? (
                              <a 
                                href="#" 
                                onClick={async (e) => {
                                  e.preventDefault();
                                  await handleDownload(invoice);
                                }}
                                style={{ cursor: 'pointer' }}
                              >
                                <i className="fa-solid fa-file-pdf" aria-hidden="true"></i> PDF
                              </a>
                            ) : (
                              <span className="text-muted">—</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default EventInvoices;


